// components/Footer.jsx
import React from 'react';
import { Box, Container, Grid, Typography, Link, Divider, IconButton } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { FaStar, FaFacebookF, FaTwitter, FaInstagram, FaYoutube, FaMapMarkerAlt, FaPhone, FaEnvelope } from 'react-icons/fa';

const Footer = () => {
  return (
    <Box sx={{ bgcolor: '#040d17', color: 'white', pt: 6, pb: 4 }}>
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Company Info */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FaStar size={24} color="#f5b400" />
              <Typography 
                variant="h5" 
                component={RouterLink}
                to="/"
                sx={{ 
                  ml: 1, 
                  fontWeight: 700, 
                  color: 'white',
                  textDecoration: 'none'
                }}
              >
                Stellar Cinemas
              </Typography>
            </Box>
            <Typography variant="subtitle2" sx={{ mb: 2, opacity: 0.8 }}>
              Where stories come to life
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
              <FaMapMarkerAlt style={{ marginRight: 8 }} /> 123 Main Street, Downtown
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
              <FaPhone style={{ marginRight: 8 }} /> (555) 123-4567
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
              <FaEnvelope style={{ marginRight: 8 }} /> info@stellarcinemas.com
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton 
                aria-label="Facebook" 
                component="a" 
                href="https://facebook.com" 
                target="_blank"
                sx={{ 
                  color: 'white', 
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '&:hover': { bgcolor: '#1877F2' }
                }}
                size="small"
              >
                <FaFacebookF />
              </IconButton>
              <IconButton 
                aria-label="Twitter" 
                component="a" 
                href="https://twitter.com" 
                target="_blank"
                sx={{ 
                  color: 'white', 
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '&:hover': { bgcolor: '#1DA1F2' }
                }}
                size="small"
              >
                <FaTwitter />
              </IconButton>
              <IconButton 
                aria-label="Instagram" 
                component="a" 
                href="https://instagram.com" 
                target="_blank"
                sx={{ 
                  color: 'white', 
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '&:hover': { bgcolor: '#E1306C' }
                }}
                size="small"
              >
                <FaInstagram />
              </IconButton>
              <IconButton 
                aria-label="YouTube" 
                component="a" 
                href="https://youtube.com" 
                target="_blank"
                sx={{ 
                  color: 'white', 
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '&:hover': { bgcolor: '#FF0000' }
                }}
                size="small"
              >
                <FaYoutube />
              </IconButton>
            </Box>
          </Grid>
          
          {/* Site Navigation */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Navigation
            </Typography>
            {[
              { name: 'Now Playing', path: '/now-playing' },
              { name: 'Coming Soon', path: '/coming-soon' },
              { name: 'Locations', path: '/locations' },
              { name: 'Dining', path: '/dining' },
              { name: 'Membership', path: '/membership' },
              { name: 'Events', path: '/events' },
              { name: 'About', path: '/about' }
            ].map(item => (
              <Typography 
                key={item.name}
                variant="body2" 
                component={RouterLink}
                to={item.path}
                sx={{ 
                  display: 'block', 
                  mb: 1,
                  color: 'text.secondary',
                  textDecoration: 'none',
                  '&:hover': { color: 'white' }
                }}
              >
                {item.name}
              </Typography>
            ))}
          </Grid>
          
          {/* Locations */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Locations
            </Typography>
            {[
              { 
                name: 'Downtown', 
                address: '123 Main Street, Downtown',
                phone: '(555) 123-4567'
              },
              { 
                name: 'Westside Mall', 
                address: '789 Shopping Lane, Westside',
                phone: '(555) 987-6543'
              },
              { 
                name: 'Lakefront', 
                address: '456 Lake Drive, Eastside',
                phone: '(555) 456-7890'
              }
            ].map(location => (
              <Box key={location.name} sx={{ mb: 2 }}>
                <Typography 
                  variant="body2" 
                  component={RouterLink}
                  to={`/locations/${location.name.toLowerCase()}`}
                  sx={{ 
                    fontWeight: 600,
                    color: 'white',
                    textDecoration: 'none',
                    '&:hover': { color: 'secondary.main' }
                  }}
                >
                  {location.name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {location.address}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {location.phone}
                </Typography>
              </Box>
            ))}
          </Grid>
          
          {/* Support Links */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Help & Info
            </Typography>
            {[
              { name: 'FAQ', path: '/faq' },
              { name: 'Contact Us', path: '/contact' },
              { name: 'Accessibility', path: '/accessibility' },
              { name: 'Gift Cards', path: '/gift-cards' },
              { name: 'Corporate Events', path: '/corporate-events' },
              { name: 'Careers', path: '/careers' },
              { name: 'Privacy Policy', path: '/privacy' },
              { name: 'Terms of Service', path: '/terms' }
            ].map(item => (
              <Typography 
                key={item.name}
                variant="body2" 
                component={RouterLink}
                to={item.path}
                sx={{ 
                  display: 'block', 
                  mb: 1,
                  color: 'text.secondary',
                  textDecoration: 'none',
                  '&:hover': { color: 'white' }
                }}
              >
                {item.name}
              </Typography>
            ))}
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.1)' }} />
        
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: { xs: 1, sm: 0 } }}>
            &copy; {new Date().getFullYear()} Stellar Cinemas. All rights reserved.
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Established 2010 | <Link component={RouterLink} to="/sitemap" sx={{ color: 'inherit' }}>Sitemap</Link>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;